'use client';

import React, { useEffect, useState, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { TrendingDown, TrendingUp, Minus, AlertOctagon, Download, Share2, Check, Zap } from 'lucide-react';
import { AuditResult } from '../lib/auditEngine';

const ScoreGaugeChart = dynamic(() => import('./ScoreGaugeChart'), { ssr: false });

interface ScoreCardProps {
  result: AuditResult;
  url: string;
}

function getScoreLabel(score: number): { label: string; color: string; description: string } {
  if (score >= 80) return { label: 'Low Friction', color: 'var(--severity-pass)', description: 'Your page is well-optimized. Focus on the remaining issues to maximize conversions.' };
  if (score >= 60) return { label: 'Moderate Friction', color: 'var(--severity-warning)', description: 'Several conversion barriers detected. Address critical issues first.' };
  if (score >= 40) return { label: 'High Friction', color: '#FF8C3D', description: 'Significant friction is costing you conversions. Multiple urgent fixes needed.' };
  return { label: 'Severe Friction', color: 'var(--severity-critical)', description: 'Your landing page has critical conversion killers. Immediate action required.' };
}

export default function ScoreCard({ result, url }: ScoreCardProps) {
  const [displayScore, setDisplayScore] = useState(0);
  const [shareState, setShareState] = useState<'idle' | 'copied'>('idle');
  const [pdfLoading, setPdfLoading] = useState(false);
  const scoreInfo = getScoreLabel(result.overallScore);

  useEffect(() => {
    let frame: number;
    let start: number | null = null;
    const duration = 1200;
    const target = result.overallScore;

    const animate = (timestamp: number) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayScore(Math.round(eased * target));
      if (progress < 1) {
        frame = requestAnimationFrame(animate);
      }
    };

    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [result.overallScore]);

  const criticalCount = result.checks.filter(c => c.severity === 'critical').length;
  const warningCount = result.checks.filter(c => c.severity === 'warning').length;
  const passCount = result.checks.filter(c => c.severity === 'pass').length;

  const cleanUrl = url.replace(/^https?:\/\//, '').replace(/\/$/, '');

  const handleShare = useCallback(async () => {
    const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareState('copied');
      setTimeout(() => setShareState('idle'), 2500);
    } catch {
      // Fallback: prompt
      if (typeof window !== 'undefined') {
        window.prompt('Copy this link to share your audit:', shareUrl);
      }
    }
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    if (pdfLoading) return;
    setPdfLoading(true);
    try {
      // Build a printable HTML page and trigger browser print-to-PDF
      const criticalChecks = result.checks.filter(c => c.severity === 'critical');
      const warningChecks = result.checks.filter(c => c.severity === 'warning');
      const passChecks = result.checks.filter(c => c.severity === 'pass');

      const checkRows = result.checks.map(c => {
        const color = c.severity === 'critical' ? '#FF3D57' : c.severity === 'warning' ? '#FFBE3D' : '#3DFF8F';
        return `<tr>
          <td style="padding:8px 12px;border-bottom:1px solid #2a2a2a;color:${color};font-weight:600;text-transform:capitalize;">${c.severity}</td>
          <td style="padding:8px 12px;border-bottom:1px solid #2a2a2a;color:#e5e5e5;">${c.id.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
          <td style="padding:8px 12px;border-bottom:1px solid #2a2a2a;color:#a0a0a0;font-size:13px;">${c.finding}</td>
          <td style="padding:8px 12px;border-bottom:1px solid #2a2a2a;color:${color};font-weight:700;text-align:center;">${c.score}/10</td>
        </tr>`;
      }).join('');

      const recRows = result.recommendations.slice(0, 5).map((r, i) => `
        <div style="margin-bottom:16px;padding:14px;background:#1a1a1a;border-radius:8px;border-left:3px solid ${r.impact === 'high' ? '#FF3D57' : r.impact === 'medium' ? '#FFBE3D' : '#3DFF8F'};">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
            <span style="background:#2a2a2a;color:#a0a0a0;font-size:11px;padding:2px 8px;border-radius:4px;font-weight:700;">#${i + 1}</span>
            <span style="color:#e5e5e5;font-weight:600;font-size:14px;">${r.title}</span>
          </div>
          <p style="color:#a0a0a0;font-size:13px;margin:0 0 8px;">${r.summary}</p>
          <ul style="margin:0;padding-left:18px;color:#a0a0a0;font-size:12px;">
            ${r.steps.slice(0, 3).map(s => `<li style="margin-bottom:4px;">${s}</li>`).join('')}
          </ul>
        </div>
      `).join('');

      const metricsHtml = result.metrics ? `
        <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:24px;">
          ${[
            { label: 'LCP', value: result.metrics.lcp },
            { label: 'FCP', value: result.metrics.fcp },
            { label: 'CLS', value: result.metrics.cls },
            { label: 'TBT', value: result.metrics.tbt },
          ].filter(m => m.value).map(m => `
            <div style="background:#1a1a1a;border-radius:8px;padding:12px;text-align:center;">
              <div style="color:#a0a0a0;font-size:11px;margin-bottom:4px;">${m.label}</div>
              <div style="color:#e5e5e5;font-weight:700;font-size:16px;">${m.value}</div>
            </div>
          `).join('')}
        </div>
      ` : '';

      const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>FrictionScan Report — ${cleanUrl}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; background: #0d0d0d; color: #e5e5e5; padding: 40px; }
  @media print { body { background: white; color: #111; } }
</style>
</head>
<body>
  <div style="max-width:800px;margin:0 auto;">
    <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:32px;padding-bottom:20px;border-bottom:1px solid #2a2a2a;">
      <div>
        <h1 style="font-size:24px;font-weight:700;color:#FF3D57;margin-bottom:4px;">FrictionScan</h1>
        <p style="color:#a0a0a0;font-size:13px;">Conversion Friction Audit Report</p>
      </div>
      <div style="text-align:right;">
        <p style="color:#e5e5e5;font-weight:600;">${cleanUrl}</p>
        <p style="color:#a0a0a0;font-size:12px;">${new Date(result.auditedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
      </div>
    </div>

    <div style="display:grid;grid-template-columns:1fr 2fr;gap:24px;margin-bottom:32px;">
      <div style="background:#1a1a1a;border-radius:12px;padding:24px;text-align:center;">
        <div style="font-size:64px;font-weight:700;color:${scoreInfo.color};line-height:1;">${result.overallScore}</div>
        <div style="color:#a0a0a0;font-size:13px;margin-top:4px;">/100 Friction Score</div>
        <div style="margin-top:12px;padding:6px 12px;background:${scoreInfo.color}18;border-radius:20px;display:inline-block;">
          <span style="color:${scoreInfo.color};font-weight:600;font-size:13px;">${scoreInfo.label}</span>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px;">
        <div style="background:#1a0a0d;border-radius:8px;padding:16px;text-align:center;border:1px solid rgba(255,61,87,0.2);">
          <div style="font-size:36px;font-weight:700;color:#FF3D57;">${criticalCount}</div>
          <div style="color:#a0a0a0;font-size:12px;margin-top:4px;">Critical</div>
        </div>
        <div style="background:#0d0a00;border-radius:8px;padding:16px;text-align:center;border:1px solid rgba(255,190,61,0.2);">
          <div style="font-size:36px;font-weight:700;color:#FFBE3D;">${warningCount}</div>
          <div style="color:#a0a0a0;font-size:12px;margin-top:4px;">Warnings</div>
        </div>
        <div style="background:#001a0a;border-radius:8px;padding:16px;text-align:center;border:1px solid rgba(61,255,143,0.2);">
          <div style="font-size:36px;font-weight:700;color:#3DFF8F;">${passCount}</div>
          <div style="color:#a0a0a0;font-size:12px;margin-top:4px;">Passing</div>
        </div>
      </div>
    </div>

    ${metricsHtml}

    <h2 style="font-size:16px;font-weight:600;color:#e5e5e5;margin-bottom:16px;text-transform:uppercase;letter-spacing:0.05em;font-size:12px;color:#a0a0a0;">Signal Checks</h2>
    <table style="width:100%;border-collapse:collapse;margin-bottom:32px;background:#1a1a1a;border-radius:8px;overflow:hidden;">
      <thead>
        <tr style="background:#2a2a2a;">
          <th style="padding:10px 12px;text-align:left;color:#a0a0a0;font-size:12px;font-weight:600;">Status</th>
          <th style="padding:10px 12px;text-align:left;color:#a0a0a0;font-size:12px;font-weight:600;">Signal</th>
          <th style="padding:10px 12px;text-align:left;color:#a0a0a0;font-size:12px;font-weight:600;">Finding</th>
          <th style="padding:10px 12px;text-align:center;color:#a0a0a0;font-size:12px;font-weight:600;">Score</th>
        </tr>
      </thead>
      <tbody>${checkRows}</tbody>
    </table>

    <h2 style="font-size:12px;font-weight:600;color:#a0a0a0;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:16px;">Top Recommendations</h2>
    ${recRows}

    <div style="margin-top:32px;padding-top:20px;border-top:1px solid #2a2a2a;text-align:center;color:#666;font-size:12px;">
      Generated by FrictionScan · frictionscan.devtestifylabs.tech
    </div>
  </div>
</body>
</html>`;

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 500);
      }
    } finally {
      setPdfLoading(false);
    }
  }, [result, cleanUrl, scoreInfo, criticalCount, warningCount, passCount, pdfLoading]);

  return (
    <div
      className="rounded-2xl border p-6 lg:p-8"
      style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
        <div>
          <h2 className="text-lg font-bold text-foreground mb-1">Friction Scorecard</h2>
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-mono text-muted-foreground truncate max-w-xs">{cleanUrl}</span>
            <span className="text-xs font-mono text-muted-foreground">·</span>
            <span className="text-xs font-mono text-muted-foreground">
              {new Date(result.auditedAt).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Real data badge */}
          <div
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
            style={{ background: 'rgba(61,255,143,0.08)', color: 'var(--severity-pass)', border: '1px solid rgba(61,255,143,0.2)' }}
          >
            <Zap size={11} aria-hidden="true" />
            Live PageSpeed data
          </div>
          {/* Share button */}
          <button
            onClick={handleShare}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 hover:opacity-80 active:scale-95"
            style={{
              background: shareState === 'copied' ? 'rgba(61,255,143,0.1)' : 'var(--muted)',
              color: shareState === 'copied' ? 'var(--severity-pass)' : 'var(--muted-foreground)',
              border: `1px solid ${shareState === 'copied' ? 'rgba(61,255,143,0.3)' : 'var(--border)'}`,
            }}
            aria-label="Copy shareable link"
          >
            {shareState === 'copied' ? <Check size={11} aria-hidden="true" /> : <Share2 size={11} aria-hidden="true" />}
            {shareState === 'copied' ? 'Copied!' : 'Share'}
          </button>
          {/* PDF download button */}
          <button
            onClick={handleDownloadPdf}
            disabled={pdfLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 hover:opacity-80 active:scale-95 disabled:opacity-50"
            style={{
              background: 'rgba(255,61,87,0.1)',
              color: 'var(--severity-critical)',
              border: '1px solid rgba(255,61,87,0.2)',
            }}
            aria-label="Download scorecard as PDF"
          >
            <Download size={11} aria-hidden="true" />
            {pdfLoading ? 'Preparing…' : 'PDF'}
          </button>
        </div>
      </div>

      {/* Core Web Vitals metrics row (if available) */}
      {result.metrics && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'LCP', value: result.metrics.lcp, desc: 'Largest Contentful Paint' },
            { label: 'FCP', value: result.metrics.fcp, desc: 'First Contentful Paint' },
            { label: 'CLS', value: result.metrics.cls, desc: 'Cumulative Layout Shift' },
            { label: 'TBT', value: result.metrics.tbt, desc: 'Total Blocking Time' },
          ].filter(m => m.value).map(m => (
            <div
              key={m.label}
              className="flex flex-col items-center p-3 rounded-xl border"
              style={{ background: 'var(--muted)', borderColor: 'var(--border)' }}
            >
              <span className="text-xs font-mono text-muted-foreground mb-1">{m.label}</span>
              <span className="text-base font-bold font-mono text-foreground">{m.value}</span>
              <span className="text-xs text-muted-foreground/60 text-center leading-tight mt-0.5 hidden sm:block">{m.desc}</span>
            </div>
          ))}
          <div
            className="flex flex-col items-center p-3 rounded-xl border"
            style={{ background: 'var(--muted)', borderColor: 'var(--border)' }}
          >
            <span className="text-xs font-mono text-muted-foreground mb-1">Perf</span>
            <span
              className="text-base font-bold font-mono"
              style={{
                color: result.metrics.performanceScore >= 90 ? 'var(--severity-pass)'
                  : result.metrics.performanceScore >= 50 ? 'var(--severity-warning)' :'var(--severity-critical)',
              }}
            >
              {result.metrics.performanceScore}
            </span>
            <span className="text-xs text-muted-foreground/60 text-center leading-tight mt-0.5 hidden sm:block">Performance</span>
          </div>
        </div>
      )}

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        {/* Left: Score gauge */}
        <div className="flex flex-col items-center">
          <ScoreGaugeChart score={result.overallScore} />
          <div className="mt-4 text-center">
            <div className="flex items-baseline gap-1 justify-center">
              <span
                className="text-6xl font-bold font-mono tabular-nums"
                style={{ color: scoreInfo.color }}
              >
                {displayScore}
              </span>
              <span className="text-2xl font-mono text-muted-foreground">/100</span>
            </div>
            <div
              className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full text-sm font-semibold"
              style={{ background: `${scoreInfo.color}18`, color: scoreInfo.color }}
            >
              {result.overallScore < 50 ? (
                <TrendingDown size={14} aria-hidden="true" />
              ) : result.overallScore < 75 ? (
                <Minus size={14} aria-hidden="true" />
              ) : (
                <TrendingUp size={14} aria-hidden="true" />
              )}
              {scoreInfo.label}
            </div>
            <p className="text-sm text-muted-foreground mt-2 max-w-xs text-center">
              {scoreInfo.description}
            </p>
          </div>
        </div>

        {/* Right: Severity breakdown */}
        <div className="space-y-4">
          <div className="space-y-3">
            <div
              className="flex items-center justify-between p-4 rounded-xl border"
              style={{ background: 'var(--severity-critical-bg)', borderColor: 'rgba(255,61,87,0.25)' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,61,87,0.15)' }}>
                  <AlertOctagon size={16} style={{ color: 'var(--severity-critical)' }} aria-hidden="true" />
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--severity-critical)' }}>Critical Issues</p>
                  <p className="text-xs text-muted-foreground">Conversion killers — fix immediately</p>
                </div>
              </div>
              <span className="text-3xl font-bold font-mono tabular-nums" style={{ color: 'var(--severity-critical)' }}>{criticalCount}</span>
            </div>

            <div
              className="flex items-center justify-between p-4 rounded-xl border"
              style={{ background: 'var(--severity-warning-bg)', borderColor: 'rgba(255,190,61,0.25)' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,190,61,0.12)' }}>
                  <TrendingDown size={16} style={{ color: 'var(--severity-warning)' }} aria-hidden="true" />
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--severity-warning)' }}>Warnings</p>
                  <p className="text-xs text-muted-foreground">Friction points — address soon</p>
                </div>
              </div>
              <span className="text-3xl font-bold font-mono tabular-nums" style={{ color: 'var(--severity-warning)' }}>{warningCount}</span>
            </div>

            <div
              className="flex items-center justify-between p-4 rounded-xl border"
              style={{ background: 'var(--severity-pass-bg)', borderColor: 'rgba(61,255,143,0.2)' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(61,255,143,0.1)' }}>
                  <TrendingUp size={16} style={{ color: 'var(--severity-pass)' }} aria-hidden="true" />
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--severity-pass)' }}>Passing</p>
                  <p className="text-xs text-muted-foreground">No friction detected</p>
                </div>
              </div>
              <span className="text-3xl font-bold font-mono tabular-nums" style={{ color: 'var(--severity-pass)' }}>{passCount}</span>
            </div>
          </div>

          <div className="pt-2">
            <p className="text-xs font-mono text-muted-foreground mb-2 uppercase tracking-wider">Signal distribution</p>
            <div className="flex h-2.5 rounded-full overflow-hidden gap-0.5" aria-label="Signal distribution bar">
              {criticalCount > 0 && (
                <div className="rounded-l-full transition-all duration-700" style={{ width: `${(criticalCount / 10) * 100}%`, background: 'var(--severity-critical)' }} />
              )}
              {warningCount > 0 && (
                <div className="transition-all duration-700" style={{ width: `${(warningCount / 10) * 100}%`, background: 'var(--severity-warning)' }} />
              )}
              {passCount > 0 && (
                <div className="rounded-r-full transition-all duration-700" style={{ width: `${(passCount / 10) * 100}%`, background: 'var(--severity-pass)' }} />
              )}
            </div>
            <div className="flex justify-between mt-1.5 text-xs font-mono text-muted-foreground">
              <span>{criticalCount} critical</span>
              <span>{warningCount} warnings</span>
              <span>{passCount} passing</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}